import React, { useState, useEffect, useCallback } from "react";
import api, { API } from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { AlertTriangle, Download, CheckCircle2, Info, RefreshCw } from "lucide-react";

const CUR_LABEL = { TRY: "₺ TRY", USD: "$ USD", EUR: "€ EUR", GBP: "£ GBP" };

function yearOptions() {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3].map(String);
}

export function ReconciliationTab({ clientId, clientNo }) {
  const { user } = useAuth();
  const canApprove = ["admin", "accountant"].includes(user?.role);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get(`/clients/${clientId}/reconciliation?year=${year}`).then(({ data }) => setData(data)).catch(() => setData({ summary: [], movements: [], approvals: [] }));
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);

  const approve = async (currency) => {
    setBusy(currency);
    try {
      await api.post(`/clients/${clientId}/reconciliation/approve?year=${year}&currency=${currency}`);
      toast.success(`${year} ${currency} mutabakatı onaylandı`);
      load();
    } catch { toast.error("Onay başarısız"); } finally { setBusy(""); }
  };
  const downloadPdf = (currency) => window.open(`${API}/clients/${clientId}/reconciliation/pdf?year=${year}&currency=${currency}`, "_blank");

  const approvalFor = (currency) => (data?.approvals || []).find((a) => a.currency === currency);
  const nextYear = Number(year) + 1;

  return (
    <div className="space-y-4">
      {/* Header + year selector */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading font-semibold text-navy-900">Masraf Avansı Mutabakatı</h3>
            <p className="text-xs text-slate-400 mt-0.5">Yıl sonu bakiyesi sonraki yıla devreder. Devir yeni tahsilat değildir.</p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="recon-year-select" value={year} onChange={(e) => setYear(e.target.value)}
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white text-navy-900">
              {yearOptions().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button size="sm" variant="outline" className="rounded-xl" data-testid="recon-reload-btn" onClick={load}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-slate-400 py-10 text-center">Yükleniyor…</p>
      ) : (data.summary || []).length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">Bu yıl için avans/masraf hareketi yok.</p>
      ) : (
        (data.summary || []).map((row) => {
          const ap = approvalFor(row.currency);
          const needsUpdate = ap && ap.status === "needs_update";
          const approved = ap && ap.status === "approved";
          return (
            <div key={row.currency} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`recon-card-${row.currency}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading font-semibold text-navy-900">{CUR_LABEL[row.currency] || row.currency}</span>
                {approved && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" />Onaylandı</span>}
                {needsUpdate && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 px-2 py-1 rounded-full"><AlertTriangle className="w-3.5 h-3.5" />Güncelleme Gerekiyor</span>}
              </div>

              <div className="divide-y divide-slate-100">
                <ReconLine label={`Önceki Yıldan Devir (01.01.${year})`} value={fmtMoney(row.opening, row.currency)} />
                <ReconLine label={`${year} Yılında Alınan Avans`} value={`+ ${fmtMoney(row.advances, row.currency)}`} positive />
                <ReconLine label={`${year} Yılında Yapılan Masraf`} value={`− ${fmtMoney(row.expenses, row.currency)}`} negative />
                <ReconLine label={`31.12.${year} Bakiye`} value={fmtMoney(row.closing, row.currency)} bold />
                <ReconLine label={`${nextYear} Yılına Devreden`} value={fmtMoney(row.closing, row.currency)} bold highlight />
              </div>

              {needsUpdate && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid={`recon-warn-${row.currency}`}>
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Bu dönem için mutabakat onaylanmıştı ancak sonrasında avans/masraf değişti. Değerler güncellendi; tekrar onaylayabilirsiniz.</span>
                </div>
              )}
              {approved && (
                <p className="text-xs text-slate-400">Onaylayan: {ap.approved_by || "—"} · {ap.approved_at ? fmtDate(ap.approved_at) : ""}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {canApprove && (
                  <Button size="sm" className="rounded-xl bg-navy-900 hover:bg-navy-800 text-white" disabled={busy === row.currency}
                    data-testid={`recon-approve-${row.currency}`} onClick={() => approve(row.currency)}>
                    {busy === row.currency ? "Onaylanıyor…" : (approved ? "Yeniden Onayla" : "Mutabakatı Onayla")}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-xl" data-testid={`recon-pdf-${row.currency}`} onClick={() => downloadPdf(row.currency)}>
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
              </div>
            </div>
          );
        })
      )}

      {/* Year movements */}
      {data && (data.movements || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h4 className="font-heading font-semibold text-navy-900 mb-1">{year} Yılı Hareketleri</h4>
          <div className="flex items-start gap-2 text-xs text-slate-400 mb-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Devreden bakiye teknik bir devirdir; tahsilat raporlarına ve yıl içi alınan avansa dahil edilmez.</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.movements.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-navy-900 truncate">{m.type_label}</p>
                  <p className="text-xs text-slate-400 truncate">{fmtDate(m.date)} · {m.description || "—"}</p>
                </div>
                <span className={`font-mono text-sm font-semibold shrink-0 ${m.kind === "advance" ? "text-emerald-600" : "text-brand-red"}`}>
                  {m.kind === "advance" ? "+" : "−"}{fmtMoney(m.amount, m.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReconLine({ label, value, positive, negative, bold, highlight }) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${highlight ? "-mx-2 px-2 rounded-lg bg-slate-50" : ""}`}>
      <span className={`text-sm ${bold ? "font-semibold text-navy-900" : "text-slate-600"}`}>{label}</span>
      <span className={`font-mono text-sm ${bold ? "font-bold text-navy-900" : positive ? "text-emerald-600" : negative ? "text-brand-red" : "text-navy-900"}`}>{value}</span>
    </div>
  );
}
